interface GuestRoadmap {
  subject: string;
  slug: string;
  modules: Array<{
    title: string;
    lessons: string[];
  }>;
  preferences: {
    level: string;
    goal: string;
    timeCommitment: string;
  };
  completedLessons: { [key: string]: boolean };
  lessonContents: { [key: string]: any };
  createdAt: string;
}

interface GuestLessonContent {
  content: string;
  moduleTitle: string;
  resourceLinks?: any[];
}

const GUEST_STORAGE_KEY = 'diy_learn_it_guest_data';

function slugify(text: string): string {
  const timestamp = Date.now().toString(36);
  return `${text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')}-${timestamp}`;
}

export const guestStorageService = {
  saveRoadmap(subject: string, modules: any[], preferences: any): string {
    try {
      const existingData = this.getAllRoadmaps();
      const slug = slugify(subject);
      
      const newRoadmap: GuestRoadmap = {
        subject,
        slug,
        modules,
        preferences,
        completedLessons: {},
        lessonContents: {},
        createdAt: new Date().toISOString()
      };

      const updatedData = [...existingData, newRoadmap];
      localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(updatedData));
      
      return slug;
    } catch (error) {
      console.error('Error saving guest roadmap:', error);
      throw error;
    }
  },

  getAllRoadmaps(): GuestRoadmap[] {
    try {
      const data = localStorage.getItem(GUEST_STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error getting guest roadmaps:', error);
      return [];
    }
  },

  getRoadmapBySlug(slug: string): GuestRoadmap | null {
    try {
      const roadmaps = this.getAllRoadmaps();
      return roadmaps.find(r => r.slug === slug) || null;
    } catch (error) {
      console.error('Error getting guest roadmap by slug:', error);
      return null;
    }
  },

  updateLessonCompletion(slug: string, lessonId: string, completed: boolean): void {
    try {
      const roadmaps = this.getAllRoadmaps();
      const updatedRoadmaps = roadmaps.map(roadmap => {
        if (roadmap.slug === slug) {
          return {
            ...roadmap,
            completedLessons: {
              ...roadmap.completedLessons,
              [lessonId]: completed
            }
          };
        }
        return roadmap;
      });

      localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(updatedRoadmaps));
    } catch (error) {
      console.error('Error updating guest lesson completion:', error);
      throw error;
    }
  },

  saveLessonContent(slug: string, lessonId: string, content: GuestLessonContent): void {
    try {
      const roadmaps = this.getAllRoadmaps();
      const updatedRoadmaps = roadmaps.map(roadmap => {
        if (roadmap.slug === slug) {
          return {
            ...roadmap,
            lessonContents: {
              ...roadmap.lessonContents,
              [lessonId]: content
            }
          };
        }
        return roadmap;
      });

      localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(updatedRoadmaps));
    } catch (error) {
      console.error('Error saving guest lesson content:', error);
      throw error;
    }
  },

  getLessonContent(slug: string, lessonId: string): GuestLessonContent | null {
    try {
      const roadmap = this.getRoadmapBySlug(slug);
      return roadmap?.lessonContents?.[lessonId] || null;
    } catch (error) {
      console.error('Error getting guest lesson content:', error);
      return null;
    }
  },

  deleteRoadmap(slug: string): void {
    try {
      const roadmaps = this.getAllRoadmaps();
      const updatedRoadmaps = roadmaps.filter(r => r.slug !== slug);
      localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(updatedRoadmaps));
    } catch (error) {
      console.error('Error deleting guest roadmap:', error);
      throw error;
    }
  },

  clearAllData(): void {
    try {
      localStorage.removeItem(GUEST_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing guest data:', error);
    }
  }
};